import React from "react";
import PosterArt from "./PosterArt";
import Wordmark from "./Wordmark";
import TasteSignal from "./TasteSignal";

// The identity artifact. Emotionally warmer than the arena — anchor artwork
// supplies the atmosphere while a thin red/cyan hairline retains TASTE TEST DNA.
// Reveals use CSS animations (animation-fill both) so content always ends in a
// visible final state and reduced-motion simply jumps to it.

function SectionLabel({ children }) {
  return (
    <p className="font-mono text-[10px] font-bold tracking-[0.28em] uppercase text-subtle mb-3">
      {children}
    </p>
  );
}

// Reveals end in a visible final state. When the user prefers reduced motion
// (or ?nomotion=1 is set) we skip the entrance entirely and render statically.
const STATIC_REVEAL =
  typeof window !== "undefined" &&
  ((window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches) ||
    new URLSearchParams(window.location.search).get("nomotion") === "1");

function Reveal({ i = 0, className = "", children }) {
  if (STATIC_REVEAL) {
    return <div className={className}>{children}</div>;
  }
  return (
    <div
      className={`animate-tt-fade-up ${className}`}
      style={{ animationDelay: `${100 + i * 90}ms` }}
    >
      {children}
    </div>
  );
}

export default function TasteProfileView({ profile, onCompare, onShareCard, onRestart, keepers, onSeeKeepers, authUser, onSaveTaste, onLogout, primaryLabel = "Compare with a friend", showGuessLock = false, scopeNav = null }) {
  const backdrop = profile.culturalAnchors?.[0]?.work;
  const signalHeading =
    !profile.scopeLabel || profile.scope === "CROSS-MEDIA"
      ? "Your Taste Signal"
      : `Your ${profile.scopeLabel} Taste Signal`;

  return (
    <div
      data-testid="taste-profile"
      className="relative min-h-[100dvh] w-full max-w-[520px] mx-auto overflow-x-hidden"
    >
      {/* Atmospheric anchor backdrop */}
      {backdrop && (
        <div aria-hidden className="pointer-events-none fixed inset-0 z-0 max-w-[520px] mx-auto">
          <div className="absolute inset-0 opacity-[0.14]">
            <PosterArt work={backdrop} showMeta={false} />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-canvas/75 via-canvas/94 to-canvas" />
        </div>
      )}

      <div className="relative z-10 px-5 pt-[calc(env(safe-area-inset-top)+22px)] pb-44">
        {/* Header + retained DNA hairline */}
        <Reveal i={0} className="flex items-center justify-between">
          <Wordmark variant="horizontal" className="h-3.5" />
          <span className="font-mono text-[10px] tracking-[0.2em] text-subtle uppercase">
            {profile.scopeLabel ? `Your ${profile.scopeLabel} taste` : "Your Profile"}
          </span>
        </Reveal>
        <Reveal i={0}>
          <div
            className="mt-3 h-px w-full"
            style={{ background: "linear-gradient(90deg, var(--tt-left), var(--tt-right))", opacity: 0.7 }}
          />
        </Reveal>

        {/* Optional Profile-scope navigation (multiple eligible Taste Signal
            cards from accumulated evidence) — supplied by the caller so this
            view's own layout/backdrop/CTA stay untouched when unused. */}
        {scopeNav && <Reveal i={0} className="mt-4">{scopeNav}</Reveal>}

        {/* Hero synthesis */}
        <Reveal i={1} className="mt-8">
          <SectionLabel>Your taste in one line</SectionLabel>
          <p
            data-testid="profile-oneline"
            className="font-display font-black leading-[1.15] tracking-[-0.02em] text-bone text-2xl sm:text-3xl"
          >
            {profile.oneLine}
          </p>
        </Reveal>

        {/* Taste Signal — a bipolar fingerprint from existing evidence.
            Omitted automatically when fewer than 3 well-supported signals. */}
        {profile.tasteSignal && profile.tasteSignal.length >= 3 && (
          <Reveal i={2} className="mt-10">
            <TasteSignal rows={profile.tasteSignal} heading={signalHeading} onSignal={onSeeKeepers} />
          </Reveal>
        )}

        {/* YOUR KEEPERS preview — the evidence behind the signal. "The Profile
            explains the pattern; the Keepers show where it came from." */}
        {onSeeKeepers && keepers && keepers.total > 0 && (
          <Reveal i={2} className="mt-10">
            <button
              type="button"
              data-testid="keepers-preview"
              onClick={() => onSeeKeepers()}
              className="block w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <SectionLabel>Your Keepers</SectionLabel>
                <span className="font-body text-[10px] tracking-[0.16em] uppercase text-white/70 group-hover:text-white/90 mb-3">
                  Explore your Keepers →
                </span>
              </div>
              <div className="flex gap-2">
                {keepers.keepers.slice(0, 5).map((k) => (
                  <div key={k.workId} className="relative flex-1 aspect-[2/3] rounded-md overflow-hidden ring-1 ring-white/10">
                    <PosterArt work={{ id: k.workId, title: k.title, domain: k.domain, year: k.year, poster: k.poster }} showMeta={false} />
                  </div>
                ))}
              </div>
              <p className="mt-2.5 font-body text-[13px] text-silver">
                {keepers.total} {keepers.total === 1 ? "work has" : "works have"} survived your choices.
              </p>
            </button>
          </Reveal>
        )}

        {/* Core signals — patterns, not scores */}
        <Reveal i={3} className="mt-10">
          <SectionLabel>Core signals</SectionLabel>
          <div className="space-y-4" data-testid="profile-signals">
            {profile.coreSignals.map((s, i) => (
              <div
                key={s.key || s.label}
                className="pl-3.5 border-l-2"
                style={{ borderColor: i % 2 === 0 ? "var(--tt-left)" : "var(--tt-right)" }}
              >
                <p className="font-display font-bold text-[15px] text-bone leading-snug">{s.label}</p>
                {s.detail && (
                  <p className="font-body text-[13px] text-silver leading-snug mt-1">{s.detail}</p>
                )}
              </div>
            ))}
          </div>
        </Reveal>

        {/* Cultural anchors */}
        <Reveal i={3} className="mt-10">
          <SectionLabel>Cultural anchors</SectionLabel>
          <div className="space-y-3" data-testid="profile-anchors">
            {profile.culturalAnchors.map((a) => (
              <div
                key={a.work.id}
                className="flex items-stretch gap-3 rounded-2xl bg-white/[0.06] border border-white/12 overflow-hidden"
              >
                <div className="relative w-16 shrink-0 aspect-[2/3]">
                  <PosterArt work={a.work} showMeta={false} />
                </div>
                <div className="flex-1 py-3 pr-3">
                  <h4 className="font-display font-bold text-sm text-bone leading-tight">
                    {a.work.title}
                  </h4>
                  <p className="font-mono text-[9px] tracking-[0.18em] text-subtle uppercase mt-0.5">
                    {a.work.domain} · {a.work.year}
                  </p>
                  <p className="font-body text-[13px] text-silver leading-snug mt-1.5">
                    {a.line}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Contradiction + Blind spot — omitted when evidence is weak */}
        {(profile.contradiction || profile.blindSpot) && (
          <Reveal i={4} className="mt-10 grid grid-cols-1 gap-3">
            {profile.contradiction && (
              <div
                className="rounded-2xl p-4 border"
                style={{ borderColor: "var(--tt-left)", background: "rgba(255,42,85,0.1)" }}
              >
                <SectionLabel>Your contradiction</SectionLabel>
                <p data-testid="profile-contradiction" className="font-body text-sm text-bone leading-snug">
                  {profile.contradiction}
                </p>
              </div>
            )}
            {profile.blindSpot && (
              <div
                className="rounded-2xl p-4 border"
                style={{ borderColor: "var(--tt-right)", background: "rgba(0,229,255,0.1)" }}
              >
                <SectionLabel>Your blind spot</SectionLabel>
                <p data-testid="profile-blindspot" className="font-body text-sm text-bone leading-snug">
                  {profile.blindSpot}
                </p>
              </div>
            )}
          </Reveal>
        )}

        {/* One pick */}
        {profile.onePick && (
          <Reveal i={5} className="mt-10">
            <SectionLabel>The one pick</SectionLabel>
            <div className="flex items-stretch gap-4 rounded-2xl overflow-hidden bg-gradient-to-br from-white/[0.09] to-white/[0.03] border border-white/12">
              <div className="relative w-24 shrink-0 aspect-[2/3]">
                <PosterArt work={profile.onePick.work} showMeta={false} />
              </div>
              <div className="flex-1 py-4 pr-4 flex flex-col justify-center">
                <h4 className="font-display font-black text-lg text-bone leading-tight">
                  {profile.onePick.work.title}
                </h4>
                <p className="font-mono text-[9px] tracking-[0.18em] text-subtle uppercase mt-1">
                  {profile.onePick.work.domain} · {profile.onePick.work.year}
                </p>
                <p className="font-body text-[13px] text-silver leading-snug mt-2">
                  {profile.onePick.reason}
                </p>
              </div>
            </div>
          </Reveal>
        )}

        <Reveal i={6}>
          {onShareCard && profile.tasteSignal && profile.tasteSignal.length >= 3 && (
            <button
              type="button"
              data-testid="share-signal-card"
              onClick={onShareCard}
              className="mt-8 w-full rounded-full border border-white/15 bg-white/[0.04] py-3.5 font-display font-bold uppercase tracking-[0.14em] text-[12px] text-bone/90 hover:bg-white/[0.08] hover:border-white/25 transition-colors"
            >
              Share your Taste Signal
            </button>
          )}
          <button
            type="button"
            data-testid="restart-btn"
            onClick={onRestart}
            className="mt-4 w-full text-center font-body font-medium text-[11px] tracking-[0.2em] uppercase text-white/65 hover:text-white/90 transition-colors"
          >
            Take the test again
          </button>
          {authUser ? (
            <p className="mt-4 text-center font-body text-[10px] tracking-[0.14em] uppercase text-white/60">
              Saved as {authUser.name || authUser.email}
              {onLogout && (
                <>
                  {" · "}
                  <button type="button" data-testid="logout-btn" onClick={onLogout} className="underline hover:text-white/70">Sign out</button>
                </>
              )}
            </p>
          ) : (
            onSaveTaste && (
              <button
                type="button"
                data-testid="save-taste-cta"
                onClick={onSaveTaste}
                className="mt-4 w-full rounded-full py-3.5 font-display font-black uppercase tracking-[0.06em] text-[12px] text-[#05070a]"
                style={{ background: "linear-gradient(90deg, var(--tt-left), var(--tt-right))" }}
              >
                Save your taste
              </button>
            )
          )}
        </Reveal>
      </div>

      {/* Dominant social CTA */}
      <div className="fixed inset-x-0 bottom-0 z-20 max-w-[520px] mx-auto px-5 pb-[calc(env(safe-area-inset-bottom)+18px)] pt-6 bg-gradient-to-t from-canvas via-canvas/95 to-transparent">
        <button
          type="button"
          data-testid="compare-friend-cta"
          onClick={onCompare}
          className="group relative w-full rounded-full py-4 font-display font-black uppercase tracking-[0.06em] text-sm text-[#05070a] overflow-hidden active:scale-[0.99] transition-transform"
          style={{ background: "linear-gradient(90deg, var(--tt-left), var(--tt-right))" }}
        >
          {primaryLabel}
        </button>

        {/* GUESS MY TASTE = MOTIVATIONAL UNLOCK (the deliberate opposite of
            Taste Editions' surprise unlock). Visible BEFORE it is available,
            because seeing it gives the user a concrete reason to get a real
            second person in. Deliberately one restrained mono line — no XP,
            no progress bar, no level language, no mobile-game lock theatre. */}
        {showGuessLock && (
          <p
            data-testid="guess-locked-signal"
            className="mt-2.5 flex items-center justify-center gap-1.5 font-mono text-[9.5px] tracking-[0.18em] uppercase text-white/55"
          >
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
            Guess my taste · unlocks at your first Taste Match
          </p>
        )}
      </div>
    </div>
  );
}
